import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button, ButtonGroup,
  FormController,
  FormLabel, Radio,
  RadioGroup,
  SelectionControlLabel,
  DrawerBody,
  DrawerFooter
} from '@one-thd/sui-atomic-components';
import {
  useLazyDataModel, useDataModel, extend,
  arrayOf, customType, params, string, number
} from '@thd-nucleus/data-sources';
import { ProductInfo } from './ProductInfo';
import { AddToListSuccess } from './AddToListSuccess';
import { TRACK } from '../constants';

export const AddToListForm = ({
  itemId,
  quantity,
  shouldCreateList,
  listDetails,
  onClose,
  products,
  isSharedList,
  setFavoriteCount,
  favoriteCount
}) => {
  const [selectedList, setSelectedList] = useState({ listId: null, listName: null });

  const { data } = useDataModel('product', {
    variables: {
      itemId
    },
    skip: !itemId
  });
  const { product } = data || {};
  const singleProduct = [
    { itemId, quantity }
  ];

  const multipleProduct = products.map((prod) => ({
    itemId: prod.itemId,
    quantity: prod.quantity
  }));

  const [addToList, addToListResponse] = useLazyDataModel('addToList', {
    variables: {
      products: itemId ? singleProduct : multipleProduct.reverse()
    },
    fetchPolicy: 'no-cache',
    context: { withAuth: true }
  });

  useEffect(() => {
    if (addToListResponse?.error) {
      const gqlErrors = addToListResponse?.error?.graphQLErrors;
      gqlErrors.forEach((err, index) => {
        setSelectedList({
          ...selectedList,
          error: err?.msg
        });
      });
    }
  }, [addToListResponse.error]);

  useEffect(() => {
    if (addToListResponse?.data?.addToList?.id && itemId) {
      LIFE_CYCLE_EVENT_BUS.trigger(TRACK.ADD_ITEM, {
        quantity, price: product?.pricing?.value || 0, sku: itemId
      });
    }
    if (addToListResponse?.data?.addToList?.id && products && !itemId) {
      const addToListLocation = isSharedList ? 'shared list' : 'existing list';
      const listItem = products.map((prod) => ({
        listLocation: addToListLocation,
        quantity: prod.quantity || 'n/a',
        price: { basePrice: prod.price || 'n/a' },
        productInfo: { sku: prod.itemId || 'n/a' }
      }));
      LIFE_CYCLE_EVENT_BUS.trigger(TRACK.ADD_PRODUCTS, {
        listItem
      });
    }
  }, [addToListResponse?.data?.addToList]);

  const selectList = (list) => {
    setSelectedList({
      id: list?.listId,
      name: list?.listName
    });
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    await addToList({
      variables: {
        id: selectedList?.id
      }
    });
  };

  useEffect(() => {
    if (addToListResponse?.data?.addToList?.id) {
      setFavoriteCount(favoriteCount + 1);
    }
  }, [addToListResponse?.data?.addToList?.id]);

  if (addToListResponse?.data?.addToList?.id) {
    return (
      <AddToListSuccess
        listName={selectedList?.name}
        listId={addToListResponse?.data?.addToList?.id}
        onClose={onClose}
      />
    );
  }

  return (
    <>
      <DrawerBody>
        <ProductInfo itemId={itemId} />
        <Button
          className="sui-mb-4 sui-mt-12 sui-mx-6 sui-text-base sui-flex"
          variant="text"
          onClick={() => { shouldCreateList(true); }}
        >
          <u>Create New List</u>
        </Button>
        <div className="sui-pb-4 sui-px-6 sui-text-base">-or-</div>
        <FormController className="sui-px-6">
          <FormLabel id="list-names" className="sui-cursor-default sui-text-base">
            Add to An Existing List:
          </FormLabel>
          {
            selectedList?.error && (
              <span className="sui-text-danger">
                {selectedList?.error}
              </span>
            )
          }
          <div className="sui-pt-10">
            <RadioGroup
              aria-labelledby="list-names"
              name="list-names"
            >
              {(listDetails || []).map((list) => {
                return (
                  <SelectionControlLabel
                    className="sui-break-all"
                    key={list.listId}
                    value={list.listId}
                    label={list.listName}
                    onChange={(evt) => { selectList(list); }}
                  >
                    <Radio />
                  </SelectionControlLabel>
                );
              })}
            </RadioGroup>
          </div>
        </FormController>
      </DrawerBody>
      <DrawerFooter>
        <ButtonGroup orientation="vertical">
          <Button
            fullWidth
            variant="primary"
            onClick={handleSubmit}
            type="submit"
            disabled={!selectedList?.id}
            data-testid="save-list"
          >
            Add to List
          </Button>
          <Button
            fullWidth
            variant="secondary"
            onClick={onClose}
            data-testid="cancel-add-to-list"
          >
            Cancel
          </Button>
        </ButtonGroup>
      </DrawerFooter>
    </>
  );
};

AddToListForm.propTypes = {
  itemId: PropTypes.string,
  quantity: PropTypes.number,
  shouldCreateList: PropTypes.func.isRequired,
  isSharedList: PropTypes.bool.isRequired,
  listDetails: PropTypes.arrayOf(PropTypes.shape({
    listId: PropTypes.string,
    listName: PropTypes.string
  })),
  onClose: PropTypes.func.isRequired,
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    price: PropTypes.number
  })),
  setFavoriteCount: PropTypes.func.isRequired,
  favoriteCount: PropTypes.number
};

AddToListForm.defaultProps = {
  itemId: null,
  quantity: 1,
  listDetails: [],
  products: [],
  favoriteCount: null
};

AddToListForm.dataModel = extend(
  {
    addToList: params({
      id: string().isRequired(),
      products: arrayOf(
        customType('AddToListProductInput').shape({
          itemId: string(),
          quantity: number(),
        }).isRequired()
      )
    }).mutation().shape({
      id: string()
    })
  },
  {
    product: params({ itemId: string().isRequired(), dataSource: string() }).shape({
      itemId: string(),
      pricing: params({ storeId: string() }).shape({
        value: number({ float: true })
      })
    })
  },
  ProductInfo
);
